import React from 'react'
import { useForm } from '@inertiajs/react'
import FormField from '../../components/forms/FormField'
import BooleanField from '../../components/forms/BooleanField'
import RadioGroupField from '../../components/forms/RadioGroupField'
import SelectField from '../../components/forms/SelectField'
import DateSelectField from '../../components/forms/DateSelectField'
import FormSection from '../../components/forms/FormSection'
import ErrorSummary from '../../components/forms/ErrorSummary'

// Options from Varejo model
const USOS_DO_SOLO = [
  'Acesso Comercial',
  'Acesso Residencial',
  'Alimentação. Auto serviço (restaurante a quilo, fast foods)',
  'Alimentação. Sem auto serviço - restaurantes, lanchonetes, botecos, padarias',
  'Artigos Diversos',
  'Artigos do lar e mercadorias em geral',
  'Artigos farmacêuticos, médicos, ortopédicos, de perfumaria e cosméticos',
  'Automotivos (Comércio)',
  'Automotivos (Serviço)',
  'Combustíveis e lubrificantes',
  'Eletromóveis',
  'Em obra',
  'Equipamentos e materiais para escritório, informática e comunicação (lojas de celulares)',
  'Estacionamento',
  'Galeria (Não há comércio voltado para a rua)',
  'Hipermercados, supermercados, produtos alimentícios, bebidas e fumo',
  'Imóvel vago (Aluguel ou venda)',
  'Livros, jornais, revistas e papelaria',
  'Lojinhas de alimentos: minimercados, açougues, hortifrutis, cereais',
  'Material de construção',
  'Órgãos públicos',
  'Óticas, jóias, bijoux, bolsas e acessórios',
  'Pet Shop',
  'Residencial',
  'Serviços Diversos',
  'Serviços Educacionais',
  'Serviços Financeiros',
  'Serviços Médicos',
  'Serviços Pessoais',
  'Shopping Center',
  'Tecidos, vestuário, calcados, artigos esportivos',
  'Templo, Igreja'
]

const USOS_DE_IMOVEL = [
  'Apartamento em condomínio',
  'Armazéns gerais e depósitos',
  'Asilo, orfanato, creche, seminário ou convento',
  'Cinema, teatro, casa de diversão, clube ou congênere',
  'Clube esportivo',
  'Cortiço',
  'Escola',
  'Escritório/consultório em condomínio (unidade autônoma)',
  'Estação radioemissora, de televisão ou empresa jornalística',
  'Flat de uso comercial (semelhante a hotel)',
  'Flat residencial em condomínio',
  'Garagem (exclusive em prédio em condomínio)',
  'Garagem (unidade autônoma) de prédio de garagens',
  'Garagem (unidade autônoma) em edifício em condomínio de escritórios, consultórios ou misto',
  'Garagem (unidade autônoma) em edifício em condomínio de uso exclusivamente residencial',
  'Hospital, ambulatório, casa de saúde e assemelhados',
  'Hotel, pensão ou hospedaria',
  'Indústria',
  'Loja',
  'Loja e residência (predominância comercial)',
  'Loja em edifício em condomínio (unidade autônoma)',
  'Oficina',
  'Outras edificações de uso coletivo, com utilização múltipla',
  'Outras edificações de uso comercial, com utilização múltipla',
  'Outras edificações de uso de serviço, com utilização múltipla',
  'Outras edificações de uso especial, com utilização múltipla',
  'Posto de serviço',
  'Prédio de apartamento, não em condomínio, de uso exclusivamente residencial',
  'Prédio de apartamento, não em condomínio, de uso misto (apartamentos e escritórios e/ou consultórios), com ou sem loja (predominância residencial)',
  'Prédio de escritório ou consultório, não em condomínio, com ou sem lojas',
  'Prédio de escritório, não em condomínio, de uso misto (apartamentos e escritórios e/ou consultórios) com ou sem loja (predominância comercial)',
  'Residência',
  'Residência coletiva, exclusive cortiço (mais de uma residência no lote)',
  'Residência e outro uso (predominância residencial)',
  'Templo',
  'Terreno'
]

const USOS_DE_TERRENO = [
  'De duas ou mais',
  'De esquina',
  'Lote de esquina',
  'Lote de fundos',
  'Lote encravado',
  'Normal',
  'Terreno interno'
]

export default function VarejoForm({ varejo, task, errors = {}, onSubmitStart, onSubmitFinish }) {
  const actionUrl = task === 'input_data'
    ? `/varejos/${varejo.id}/input_data`
    : `/varejos/${varejo.id}/revise`

  const { data, setData, patch, processing } = useForm({
    task: task,
    varejo: {
      // Text fields - use empty string fallback
      nome: varejo.nome || '',
      no_imovel: varejo.no_imovel || '',
      data_levantamento: varejo.data_levantamento || '',
      tipo_de_uso: varejo.tipo_de_uso || '',
      tipo_de_terreno: varejo.tipo_de_terreno || '',
      cultura_comercial_diversificada: varejo.cultura_comercial_diversificada || '',
      uso_terreo: varejo.uso_terreo || '',
      // Number fields - use empty string fallback (will be converted to null by Rails)
      valor_venal_lote: varejo.valor_venal_lote ?? '',
      valor_venal_edificio: varejo.valor_venal_edificio ?? '',
      area_construida: varejo.area_construida ?? '',
      area_do_terreno: varejo.area_do_terreno ?? '',
      estacionamento_meio_fio: varejo.estacionamento_meio_fio ?? '',
      estacionamento_recuo: varejo.estacionamento_recuo ?? '',
      estacionamento_garagem: varejo.estacionamento_garagem ?? '',
      estacionamento_moto: varejo.estacionamento_moto ?? '',
      idade_aproximada: varejo.idade_aproximada ?? '',
      // Boolean fields - keep as-is
      estacionamento_zona_azul: varejo.estacionamento_zona_azul,
      ponto_taxi: varejo.ponto_taxi,
      fruicao_publica: varejo.fruicao_publica,
      entrada_de_veiculos: varejo.entrada_de_veiculos,
      ponto_de_onibus: varejo.ponto_de_onibus,
      edificacao_tombada: varejo.edificacao_tombada,
      arborizacao: varejo.arborizacao,
      ambulantes: varejo.ambulantes,
      degrau_entre_calcadas: varejo.degrau_entre_calcadas,
      permanencia_na_calcada: varejo.permanencia_na_calcada,
      muro: varejo.muro,
      paraciclo: varejo.paraciclo,
      horario_comercial: varejo.horario_comercial,
      fim_de_semana: varejo.fim_de_semana,
      apos_19: varejo.apos_19,
      // Enum fields - use null, NOT empty string (PostgreSQL enums reject "")
      fachada_ativa: varejo.fachada_ativa || null,
      uso_superior: varejo.uso_superior || null,
      testada: varejo.testada || null,
      gabarito: varejo.gabarito || null,
      protecao_sol_chuva: varejo.protecao_sol_chuva || null,
      conservacao_fachada: varejo.conservacao_fachada || null,
      conservacao_calcada: varejo.conservacao_calcada || null,
      alugar_ou_vender: varejo.alugar_ou_vender || null,
    },
  })

  const handleSubmit = (e) => {
    e.preventDefault()
    onSubmitStart?.()
    patch(actionUrl, {
      forceFormData: true,
      onFinish: () => onSubmitFinish?.(),
    })
  }

  const updateVarejo = (field, value) => {
    setData('varejo', { ...data.varejo, [field]: value })
  }

  return (
    <form onSubmit={handleSubmit}>
      <ErrorSummary errors={errors} />
      <div className="space-y-4">
        {/* Section 1: Basic Info */}
        <FormSection title="Informações Básicas" defaultOpen={true}>
          <FormField
            label="Nome do comércio"
            name="varejo[nome]"
            value={data.varejo.nome}
            onChange={(e) => updateVarejo('nome', e.target.value)}
            error={errors.nome}
          />

          <FormField
            label="Número do imóvel (número da porta)"
            name="varejo[no_imovel]"
            value={data.varejo.no_imovel}
            onChange={(e) => updateVarejo('no_imovel', e.target.value)}
            error={errors.no_imovel}
          />

          <DateSelectField
            label="Data do levantamento"
            name="varejo[data_levantamento]"
            value={data.varejo.data_levantamento}
            onChange={(e) => updateVarejo('data_levantamento', e.target.value)}
            error={errors.data_levantamento}
          />
        </FormSection>

        {/* Section 2: IPTU Data */}
        <FormSection title="Dados IPTU">
          <SelectField
            label="Tipo de uso segundo o IPTU"
            name="varejo[tipo_de_uso]"
            options={USOS_DE_IMOVEL}
            value={data.varejo.tipo_de_uso}
            onChange={(e) => updateVarejo('tipo_de_uso', e.target.value)}
            error={errors.tipo_de_uso}
          />

          <SelectField
            label="Tipo de terreno segundo o IPTU"
            name="varejo[tipo_de_terreno]"
            options={USOS_DE_TERRENO}
            value={data.varejo.tipo_de_terreno}
            onChange={(e) => updateVarejo('tipo_de_terreno', e.target.value)}
            error={errors.tipo_de_terreno}
          />

          <FormField
            label="Valor venal do lote segundo o IPTU (R$)"
            name="varejo[valor_venal_lote]"
            type="number"
            value={data.varejo.valor_venal_lote}
            onChange={(e) => updateVarejo('valor_venal_lote', e.target.value)}
            error={errors.valor_venal_lote}
          />

          <FormField
            label="Valor venal do edifício segundo o IPTU (R$)"
            name="varejo[valor_venal_edificio]"
            type="number"
            value={data.varejo.valor_venal_edificio}
            onChange={(e) => updateVarejo('valor_venal_edificio', e.target.value)}
            error={errors.valor_venal_edificio}
          />

          <FormField
            label="Área construída em m²"
            name="varejo[area_construida]"
            type="number"
            value={data.varejo.area_construida}
            onChange={(e) => updateVarejo('area_construida', e.target.value)}
            error={errors.area_construida}
          />

          <FormField
            label="Área do terreno em m²"
            name="varejo[area_do_terreno]"
            type="number"
            value={data.varejo.area_do_terreno}
            onChange={(e) => updateVarejo('area_do_terreno', e.target.value)}
            error={errors.area_do_terreno}
          />
        </FormSection>

        {/* Section 3: Parking */}
        <FormSection title="Estacionamento">
          <FormField
            label="Quantas vagas de estacionamento no meio fio?"
            name="varejo[estacionamento_meio_fio]"
            type="number"
            value={data.varejo.estacionamento_meio_fio}
            onChange={(e) => updateVarejo('estacionamento_meio_fio', e.target.value)}
            error={errors.estacionamento_meio_fio}
          />

          <BooleanField
            label="Tais vagas de estacionamento são do tipo 'Zona azul'?"
            name="varejo[estacionamento_zona_azul]"
            value={data.varejo.estacionamento_zona_azul}
            onChange={(val) => updateVarejo('estacionamento_zona_azul', val)}
            error={errors.estacionamento_zona_azul}
          />

          <FormField
            label="Quantas vagas de estacionamento no recuo?"
            name="varejo[estacionamento_recuo]"
            type="number"
            value={data.varejo.estacionamento_recuo}
            onChange={(e) => updateVarejo('estacionamento_recuo', e.target.value)}
            error={errors.estacionamento_recuo}
          />

          <FormField
            label="Quantas vagas de estacionamento em garagem?"
            name="varejo[estacionamento_garagem]"
            type="number"
            value={data.varejo.estacionamento_garagem}
            onChange={(e) => updateVarejo('estacionamento_garagem', e.target.value)}
            error={errors.estacionamento_garagem}
          />

          <FormField
            label="Quantas vagas de estacionamento para motos?"
            name="varejo[estacionamento_moto]"
            type="number"
            value={data.varejo.estacionamento_moto}
            onChange={(e) => updateVarejo('estacionamento_moto', e.target.value)}
            error={errors.estacionamento_moto}
          />

          <BooleanField
            label="Há ponto de táxi?"
            name="varejo[ponto_taxi]"
            value={data.varejo.ponto_taxi}
            onChange={(val) => updateVarejo('ponto_taxi', val)}
            error={errors.ponto_taxi}
          />
        </FormSection>

        {/* Section 4: Facade & Use */}
        <FormSection title="Fachada e Uso">
          <RadioGroupField
            label="Há fachada ativa?"
            name="varejo[fachada_ativa]"
            options={{ 'Não': 'nao', 'Vitrine': 'vitrine', 'Porta aberta': 'porta_aberta' }}
            value={data.varejo.fachada_ativa}
            onChange={(val) => updateVarejo('fachada_ativa', val)}
            error={errors.fachada_ativa}
          />

          <BooleanField
            label="Há fruição pública?"
            name="varejo[fruicao_publica]"
            value={data.varejo.fruicao_publica}
            onChange={(val) => updateVarejo('fruicao_publica', val)}
            error={errors.fruicao_publica}
          />

          <FormField
            label="Se comercializa produtos étnicos e religiosos, especificar as culturas contempladas (separar por vírgulas)"
            name="varejo[cultura_comercial_diversificada]"
            as="textarea"
            value={data.varejo.cultura_comercial_diversificada}
            onChange={(e) => updateVarejo('cultura_comercial_diversificada', e.target.value)}
            error={errors.cultura_comercial_diversificada}
          />

          <SelectField
            label="Uso do solo (terreo)"
            name="varejo[uso_terreo]"
            options={USOS_DO_SOLO}
            value={data.varejo.uso_terreo}
            onChange={(e) => updateVarejo('uso_terreo', e.target.value)}
            error={errors.uso_terreo}
          />

          <BooleanField
            label="Há entrada de veículos?"
            name="varejo[entrada_de_veiculos]"
            value={data.varejo.entrada_de_veiculos}
            onChange={(val) => updateVarejo('entrada_de_veiculos', val)}
            error={errors.entrada_de_veiculos}
          />

          <BooleanField
            label="Há ponto de ônibus?"
            name="varejo[ponto_de_onibus]"
            value={data.varejo.ponto_de_onibus}
            onChange={(val) => updateVarejo('ponto_de_onibus', val)}
            error={errors.ponto_de_onibus}
          />

          <RadioGroupField
            label="Utilização dos pavimentos superiores"
            name="varejo[uso_superior]"
            options={{ 'Não há': 'nao', 'Residencial': 'residencial', 'Comércio': 'comercio', 'Serviços': 'servicos' }}
            value={data.varejo.uso_superior}
            onChange={(val) => updateVarejo('uso_superior', val)}
            error={errors.uso_superior}
          />
        </FormSection>

        {/* Section 5: Building */}
        <FormSection title="Edificação">
          <RadioGroupField
            label="Testada do edifício, em metros"
            name="varejo[testada]"
            options={{ 'Entre 0 e 5m': '0-5m', 'Entre 5 e 10m': '5-10m', 'Maior do que 10m': '>10m' }}
            value={data.varejo.testada}
            onChange={(val) => updateVarejo('testada', val)}
            error={errors.testada}
          />

          <RadioGroupField
            label="Gabarito do edifício, em número de andares"
            name="varejo[gabarito]"
            options={{ 'Entre 0 e 6': '0-6', 'Entre 7 e 14': '7-14', 'Maior do que 14': '>14' }}
            value={data.varejo.gabarito}
            onChange={(val) => updateVarejo('gabarito', val)}
            error={errors.gabarito}
          />

          <FormField
            label="Idade aproximada dos edifícios, em anos"
            name="varejo[idade_aproximada]"
            type="number"
            value={data.varejo.idade_aproximada}
            onChange={(e) => updateVarejo('idade_aproximada', e.target.value)}
            error={errors.idade_aproximada}
          />

          <BooleanField
            label="Trata-se de uma edificação tombada?"
            name="varejo[edificacao_tombada]"
            value={data.varejo.edificacao_tombada}
            onChange={(val) => updateVarejo('edificacao_tombada', val)}
            error={errors.edificacao_tombada}
          />
        </FormSection>

        {/* Section 6: Environment */}
        <FormSection title="Ambiente e Calçada">
          <BooleanField
            label="Há árvores que sombreiam?"
            name="varejo[arborizacao]"
            value={data.varejo.arborizacao}
            onChange={(val) => updateVarejo('arborizacao', val)}
            error={errors.arborizacao}
          />

          <RadioGroupField
            label="Há proteção de sol e chuva? Marcar o tipo"
            name="varejo[protecao_sol_chuva]"
            options={{ 'Não': 'nao', 'Beiral': 'beiral', 'Toldo': 'toldo', 'Marquise': 'marquise', 'Outros': 'outros' }}
            value={data.varejo.protecao_sol_chuva}
            onChange={(val) => updateVarejo('protecao_sol_chuva', val)}
            error={errors.protecao_sol_chuva}
          />

          <BooleanField
            label="Há vendedores ambulantes?"
            name="varejo[ambulantes]"
            value={data.varejo.ambulantes}
            onChange={(val) => updateVarejo('ambulantes', val)}
            error={errors.ambulantes}
          />

          <RadioGroupField
            label="Estado de conservação da fachada"
            name="varejo[conservacao_fachada]"
            options={{ 'Ótimo': 'otimo', 'Bom': 'bom', 'Ruim': 'ruim' }}
            value={data.varejo.conservacao_fachada}
            onChange={(val) => updateVarejo('conservacao_fachada', val)}
            error={errors.conservacao_fachada}
          />

          <RadioGroupField
            label="Estado de conservação da calçada"
            name="varejo[conservacao_calcada]"
            options={{ 'Ótimo': 'otimo', 'Bom': 'bom', 'Ruim': 'ruim' }}
            value={data.varejo.conservacao_calcada}
            onChange={(val) => updateVarejo('conservacao_calcada', val)}
            error={errors.conservacao_calcada}
          />

          <BooleanField
            label="Há degrau ou desnível entre as calçadas?"
            name="varejo[degrau_entre_calcadas]"
            value={data.varejo.degrau_entre_calcadas}
            onChange={(val) => updateVarejo('degrau_entre_calcadas', val)}
            error={errors.degrau_entre_calcadas}
          />

          <BooleanField
            label="Há permanência na calçada? (lugares para estar ou sentar)"
            name="varejo[permanencia_na_calcada]"
            value={data.varejo.permanencia_na_calcada}
            onChange={(val) => updateVarejo('permanencia_na_calcada', val)}
            error={errors.permanencia_na_calcada}
          />

          <BooleanField
            label="Há muro ou fachada cega com mais de 20m?"
            name="varejo[muro]"
            value={data.varejo.muro}
            onChange={(val) => updateVarejo('muro', val)}
            error={errors.muro}
          />

          <BooleanField
            label="Há paraciclo?"
            name="varejo[paraciclo]"
            value={data.varejo.paraciclo}
            onChange={(val) => updateVarejo('paraciclo', val)}
            error={errors.paraciclo}
          />
        </FormSection>

        {/* Section 7: Services */}
        <FormSection title="Serviços e Comércio">
          <RadioGroupField
            label="Há imóveis para alugar ou vender?"
            name="varejo[alugar_ou_vender]"
            options={{ 'Não': 'nao', 'Alugar': 'alugar', 'Vender': 'vender', 'Ambos': 'ambos' }}
            value={data.varejo.alugar_ou_vender}
            onChange={(val) => updateVarejo('alugar_ou_vender', val)}
            error={errors.alugar_ou_vender}
          />
        </FormSection>

        {/* Section 8: Hours */}
        <FormSection title="Horário de Funcionamento">
          <BooleanField
            label="Funciona durante o horário comercial?"
            name="varejo[horario_comercial]"
            value={data.varejo.horario_comercial}
            onChange={(val) => updateVarejo('horario_comercial', val)}
            error={errors.horario_comercial}
          />

          <BooleanField
            label="Funciona aos finais de semana?"
            name="varejo[fim_de_semana]"
            value={data.varejo.fim_de_semana}
            onChange={(val) => updateVarejo('fim_de_semana', val)}
            error={errors.fim_de_semana}
          />

          <BooleanField
            label="Funciona após as 19:00?"
            name="varejo[apos_19]"
            value={data.varejo.apos_19}
            onChange={(val) => updateVarejo('apos_19', val)}
            error={errors.apos_19}
          />
        </FormSection>
      </div>

      <button
        type="submit"
        disabled={processing}
        className="mt-6 px-6 py-2 bg-brand-dark hover:bg-brand-light text-white font-medium rounded-md transition-colors disabled:opacity-50"
      >
        {processing ? 'Atualizando...' : 'Atualizar'}
      </button>
    </form>
  )
}
